import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";

import { getArchivedCustomers } from "src/api/customer";
import { useAuth } from "src/providers/AuthProvider";
import useDebounce from "src/hooks/useDebounce";
import CustomModal from "src/components/CustomModal";
import { DataTable, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue } from "src/lib/utils";
import type { CustomerRow } from "src/types/customer";

interface ArchivedCustomersModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Opens the detail modal for a restored/inspected customer. */
	onViewCustomer: (uuid: string) => void;
}

/**
 * Archived customers as a modal (decision D2). `/customers/archived` redirects to the list
 * with `?archived=1`, which opens this.
 *
 * Its own local search/paging state rather than the URL query string, so it doesn't collide
 * with the underlying list page's params.
 */
const ArchivedCustomersModal = ({ open, onOpenChange, onViewCustomer }: ArchivedCustomersModalProps) => {
	const { user } = useAuth();
	const customerLabelPlural = getCustomizationValue(user, "customer_label") || "Customers";

	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState<RowsPerPageOption>(10);

	const handleSearch = useDebounce((term: string) => {
		setSearch(term);
		setPage(1);
	}, 500);

	const { data, isLoading } = useQuery({
		queryKey: ["customers", "archived", page, limit, search],
		queryFn: () => getArchivedCustomers({ page, limit: limit === "All" ? 9999 : limit, search }),
		enabled: open,
	});

	const list = data?.data;
	const customers = list?.data ?? [];

	const columns: Column<CustomerRow>[] = [
		{ key: "id", header: "Cust ID", cell: (row) => row.customer_id ?? "—" },
		{
			key: "name",
			header: "Name",
			primary: true,
			cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "—",
		},
		{ key: "email", header: "Email", cell: (row) => row.email ?? "—" },
		{ key: "phone", header: "Phone", cell: (row) => row.phone ?? "—" },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[90px] tw-text-right",
			cell: (row) => (
				<div onClick={(event) => event.stopPropagation()}>
					<button
						type="button"
						onClick={() => onViewCustomer(row.uuid)}
						className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
					>
						View
					</button>
				</div>
			),
		},
	];

	return (
		<CustomModal open={open} onOpenChange={onOpenChange} size="lg" title={`Archived ${customerLabelPlural}`}>
			<div className="tw-space-y-4">
				<div className="tw-relative tw-w-full sm:tw-w-[280px]">
					<Search className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-text-soft" />
					<input
						type="search"
						placeholder={`Search archived ${customerLabelPlural.toLowerCase()}...`}
						onChange={(event) => handleSearch(event.target.value.trim())}
						className="tw-h-10 tw-w-full tw-rounded-[10px] tw-border tw-border-border tw-bg-white tw-pl-9 tw-pr-3 tw-text-[13.5px] tw-text-text placeholder:tw-text-text-soft focus:tw-border-teal focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal/20"
					/>
				</div>

				<DataTable
					data={customers}
					columns={columns}
					rowKey={(row) => row.uuid}
					loading={isLoading}
					totalRows={list?.total ?? customers.length}
					page={page}
					onPageChange={setPage}
					rowsPerPage={limit}
					onRowsPerPageChange={setLimit}
					onRowClick={(row) => onViewCustomer(row.uuid)}
					emptyTitle={`No archived ${customerLabelPlural.toLowerCase()}`}
					emptyDescription={`${customerLabelPlural} you archive will be listed here and can be restored.`}
				/>
			</div>
		</CustomModal>
	);
};

export default ArchivedCustomersModal;
